import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet, apiPost, apiPut } from "../../lib/api";
import { QrCodeImage } from "../../components/QrCodeImage";

type Tenant = {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended" | "onboarding";
  default_locale: string;
  plan_code?: string | null;
  notes?: string | null;
};

type TenantUser = {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
};

type Health = {
  users_total: number;
  branches_total: number;
  integrations_total: number;
  integrations_error: number;
  pending_messages: number;
  active_appointments_today: number;
};

type TenantCreateSummary = {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  ownerEmail?: string;
};

type OwnerOnboardingLink = {
  tenant_id: string;
  deep_link_url: string;
  expires_at: string;
};

function formatErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Request failed";
}

function buildOwnerInviteMessage(link: string): string {
  return `I created your salon in Salon OS. Open this Telegram invite to activate owner access: ${link} (single-use, expires in 24h). Your salon starts with the shared Salon OS Telegram bot by default, and you can connect your own bot later.`;
}

function buildTelegramShareUrl(link: string): string {
  const text = "I created your salon in Salon OS. Open this Telegram invite to activate owner access (single-use, expires in 24h). Shared Telegram bot is included by default, custom bot can be connected later.";
  return `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
}

export function SystemTenantsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [newTenantName, setNewTenantName] = useState("");
  const [newTenantSlug, setNewTenantSlug] = useState("");
  const [newTenantStatus, setNewTenantStatus] = useState<"active" | "suspended" | "onboarding">("onboarding");
  const [newBranchName, setNewBranchName] = useState("Main Branch");
  const [newBranchTimezone, setNewBranchTimezone] = useState("UTC");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [summary, setSummary] = useState<TenantCreateSummary | null>(null);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [actionFeedback, setActionFeedback] = useState("");
  const [newFlagKey, setNewFlagKey] = useState("ui_manager_v2");
  const [newFlagEnabled, setNewFlagEnabled] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userRole, setUserRole] = useState("manager");
  const [ownerOnboarding, setOwnerOnboarding] = useState<OwnerOnboardingLink | null>(null);

  const isCreateFormValid =
    !!newTenantName.trim() &&
    !!newBranchName.trim() &&
    !!newBranchTimezone.trim() &&
    (!!ownerEmail.trim() === !!ownerPassword.trim());

  const buildCreateTenantPayload = () => ({
    name: newTenantName,
    slug: newTenantSlug || null,
    default_locale: "en",
    status: newTenantStatus,
    default_branch_name: newBranchName,
    default_branch_timezone: newBranchTimezone,
    owner_email: ownerEmail.trim() || null,
    owner_password: ownerPassword.trim() || null,
  });

  const clearSelectedOnboardingState = () => {
    setOwnerOnboarding(null);
    setActionFeedback("");
  };

  const applyTenantCreateSuccess = async (tenant: Tenant) => {
    const ownerEmailValue = ownerEmail.trim() || undefined;
    setNewTenantName("");
    setNewTenantSlug("");
    setOwnerEmail("");
    setOwnerPassword("");
    setSummary({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSlug: tenant.slug,
      ownerEmail: ownerEmailValue,
    });
    setSelectedId(tenant.id);
    await qc.invalidateQueries({ queryKey: ["sys-tenants"] });
    await qc.invalidateQueries({ queryKey: ["sys-tenant-detail", tenant.id] });
    await qc.invalidateQueries({ queryKey: ["sys-tenant-users", tenant.id] });
    await qc.invalidateQueries({ queryKey: ["sys-tenant-health", tenant.id] });
  };

  const tenants = useQuery({
    queryKey: ["sys-tenants", search],
    queryFn: () =>
      apiGet<{ items: Tenant[]; meta: { total: number } }>(
        `/v3/sys/tenants?page=1&size=50${search ? `&search=${encodeURIComponent(search)}` : ""}`
      ),
  });

  const details = useQuery({
    queryKey: ["sys-tenant-detail", selectedId],
    queryFn: () => apiGet<Tenant>(`/v3/sys/tenants/${selectedId}`),
    enabled: !!selectedId,
  });
  const users = useQuery({
    queryKey: ["sys-tenant-users", selectedId],
    queryFn: () => apiGet<{ items: TenantUser[] }>(`/v3/sys/tenants/${selectedId}/users?page=1&size=50`),
    enabled: !!selectedId,
  });
  const health = useQuery({
    queryKey: ["sys-tenant-health", selectedId],
    queryFn: () => apiGet<Health>(`/v3/sys/tenants/${selectedId}/health`),
    enabled: !!selectedId,
  });

  const createTenant = useMutation({
    mutationFn: () => apiPost<Tenant>("/v3/sys/tenants", buildCreateTenantPayload()),
    onMutate: () => {
      setActionFeedback("");
      setOwnerOnboarding(null);
    },
    onSuccess: async (tenant) => {
      await applyTenantCreateSuccess(tenant);
    },
    onError: (error) => setActionFeedback(formatErrorMessage(error)),
  });

  const createTenantAndInvite = useMutation({
    mutationFn: async (): Promise<{ tenant: Tenant; onboarding: OwnerOnboardingLink | null; onboardingError: string | null }> => {
      const tenant = await apiPost<Tenant>("/v3/sys/tenants", buildCreateTenantPayload());
      try {
        const onboarding = await apiPost<OwnerOnboardingLink>(`/v3/sys/tenants/${tenant.id}/onboarding-token`, {});
        return { tenant, onboarding, onboardingError: null };
      } catch (error) {
        return { tenant, onboarding: null, onboardingError: formatErrorMessage(error) };
      }
    },
    onMutate: () => {
      setActionFeedback("");
      setOwnerOnboarding(null);
    },
    onSuccess: async ({ tenant, onboarding, onboardingError }) => {
      await applyTenantCreateSuccess(tenant);
      if (onboarding) {
        setOwnerOnboarding(onboarding);
      }
      if (onboardingError) {
        setActionFeedback(`Tenant created, but invite generation failed: ${onboardingError}`);
      }
    },
    onError: (error) => {
      setOwnerOnboarding(null);
      setActionFeedback(formatErrorMessage(error));
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: "active" | "suspended" | "onboarding") =>
      apiPut(`/v3/sys/tenants/${selectedId}/status`, { status }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["sys-tenants"] });
      await qc.invalidateQueries({ queryKey: ["sys-tenant-detail", selectedId] });
    },
  });

  const createUser = useMutation({
    mutationFn: () =>
      apiPost(`/v3/sys/tenants/${selectedId}/users`, {
        email: userEmail,
        password: userPassword,
        role: userRole,
        is_active: true,
      }),
    onSuccess: async () => {
      setUserEmail("");
      setUserPassword("");
      await qc.invalidateQueries({ queryKey: ["sys-tenant-users", selectedId] });
    },
  });

  const upsertFlag = useMutation({
    mutationFn: () =>
      apiPut(`/v3/sys/tenants/${selectedId}/feature-flags/${encodeURIComponent(newFlagKey)}`, {
        key: newFlagKey,
        enabled: newFlagEnabled,
      }),
  });

  const createOnboardingToken = useMutation({
    mutationFn: () => apiPost<OwnerOnboardingLink>(`/v3/sys/tenants/${selectedId}/onboarding-token`, {}),
    onMutate: () => {
      setActionFeedback("");
      setOwnerOnboarding(null);
    },
    onSuccess: (data) => setOwnerOnboarding(data),
    onError: (error) => {
      setOwnerOnboarding(null);
      setActionFeedback(formatErrorMessage(error));
    },
  });

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback("Copied.");
      window.setTimeout(() => setCopyFeedback(""), 1800);
    } catch {
      setCopyFeedback(`Copy failed. Value: ${value}`);
    }
  };

  const copyOwnerInviteMessage = async (link: string) => {
    await copyText(buildOwnerInviteMessage(link));
  };

  const openTelegramShare = (link: string) => {
    const url = buildTelegramShareUrl(link);
    const popup = window.open(url, "_blank", "noopener,noreferrer");
    if (!popup) {
      setActionFeedback("Telegram share popup was blocked. Use Copy Message and paste it in Telegram.");
    }
  };

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">Tenant Operations</h1>
      {copyFeedback ? <p className="text-xs text-slate-500">{copyFeedback}</p> : null}
      {actionFeedback ? <p className="text-xs text-rose-600">{actionFeedback}</p> : null}

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Create Tenant</h2>
        <div className="grid md:grid-cols-4 gap-2 mt-2">
          <input className="input" placeholder="Tenant name" value={newTenantName} onChange={(e) => setNewTenantName(e.target.value)} />
          <input className="input" placeholder="Slug (optional)" value={newTenantSlug} onChange={(e) => setNewTenantSlug(e.target.value)} />
          <input className="input" placeholder="Default branch name" value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)} />
          <input className="input" placeholder="Default branch timezone (IANA)" value={newBranchTimezone} onChange={(e) => setNewBranchTimezone(e.target.value)} />
        </div>
        <div className="grid md:grid-cols-5 gap-2 mt-2">
          <input className="input" placeholder="Owner email (optional)" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          <input className="input" type="password" placeholder="Owner password (optional)" value={ownerPassword} onChange={(e) => setOwnerPassword(e.target.value)} />
          <select
            className="input"
            value={newTenantStatus}
            onChange={(e) => setNewTenantStatus(e.target.value as "active" | "suspended" | "onboarding")}
          >
            <option value="onboarding">onboarding</option>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
          </select>
          <button
            className="btn"
            onClick={() => createTenant.mutate()}
            disabled={!isCreateFormValid || createTenant.isPending || createTenantAndInvite.isPending}
          >
            {createTenant.isPending ? "Creating..." : "Create"}
          </button>
          <button
            className="btn secondary"
            onClick={() => createTenantAndInvite.mutate()}
            disabled={!isCreateFormValid || createTenant.isPending || createTenantAndInvite.isPending}
          >
            {createTenantAndInvite.isPending ? "Creating + Invite..." : "Create + Telegram Invite"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Owner credentials are optional but must be provided together. Password policy: min 10 with upper/lower/digit.</p>
        <p className="text-xs text-slate-500 mt-1">
          If owner email/password is empty, a Telegram-only placeholder owner account will be created automatically.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          When platform Telegram bot is configured, new tenants also get a shared Telegram integration by default. Owners can upgrade to their own bot later.
        </p>
      </section>

      {summary ? (
        <section className="panel p-4">
          <h2 className="text-lg font-semibold">Onboarding Summary</h2>
          <p className="text-sm mt-2">
            <strong>{summary.tenantName}</strong> ({summary.tenantSlug})
          </p>
          <p className="text-sm mt-1">
            Tenant ID: <code>{summary.tenantId}</code>{" "}
            <button className="btn secondary" onClick={() => copyText(summary.tenantId)}>
              Copy ID
            </button>
          </p>
          <p className="text-sm mt-1">Manager login requires this tenant ID + user email/password.</p>
          {summary.ownerEmail ? <p className="text-sm mt-1">Seeded owner email: {summary.ownerEmail}</p> : null}
        </section>
      ) : null}

      <section className="panel p-4">
        <div className="flex flex-wrap justify-between gap-2 items-center">
          <h2 className="text-lg font-semibold">Tenants</h2>
          <input className="input max-w-xs" placeholder="Search name/slug" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="grid gap-2 mt-2">
          {(tenants.data?.items ?? []).map((t) => (
            <article
              key={t.id}
              className={`rounded border p-3 ${selectedId === t.id ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white"}`}
            >
              <button
                className="w-full text-left"
                onClick={() => {
                  setSelectedId(t.id);
                  clearSelectedOnboardingState();
                }}
              >
                <p className="font-semibold">{t.name}</p>
                <p className="text-xs text-slate-500">
                  {t.slug} | {t.status}
                </p>
              </button>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span>Tenant ID:</span>
                <code>{t.id}</code>
                <button className="btn secondary" onClick={() => copyText(t.id)}>
                  Copy ID
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {selectedId ? (
        <>
          <section className="panel p-4">
            <h2 className="text-lg font-semibold">Tenant Detail</h2>
            {details.data ? (
              <div className="text-sm grid gap-2 mt-2">
                <p>
                  <strong>{details.data.name}</strong> ({details.data.slug})
                </p>
                <p>
                  Tenant ID: <code>{details.data.id}</code>{" "}
                  <button className="btn secondary" onClick={() => copyText(details.data.id)}>
                    Copy ID
                  </button>
                </p>
                <p>Status: {details.data.status}</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn secondary" onClick={() => setStatus.mutate("active")}>
                  Set Active
                  </button>
                  <button className="btn secondary" onClick={() => setStatus.mutate("onboarding")}>
                    Set Onboarding
                  </button>
                <button className="btn" onClick={() => setStatus.mutate("suspended")}>
                  Suspend Tenant
                </button>
                <button className="btn secondary" onClick={() => createOnboardingToken.mutate()} disabled={createOnboardingToken.isPending}>
                  {createOnboardingToken.isPending ? "Generating..." : "Generate Owner Onboarding Link"}
                </button>
              </div>
              {ownerOnboarding && ownerOnboarding.tenant_id === selectedId ? (
                <div className="rounded border border-slate-200 bg-white p-3 grid gap-2">
                  <p className="text-sm">
                    Owner onboarding link:{" "}
                    <a href={ownerOnboarding.deep_link_url} target="_blank" rel="noreferrer" className="underline">
                      Open Link
                    </a>
                  </p>
                  <code className="text-xs break-all">{ownerOnboarding.deep_link_url}</code>
                  <p className="text-xs text-slate-500">Expires: {ownerOnboarding.expires_at}</p>
                  <p className="text-xs text-slate-500">
                    Friend must open this invite inside Telegram. It is single-use and after redemption they land in the Telegram dashboard. They do not need to create a bot before redeeming the invite.
                  </p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <button className="btn secondary" onClick={() => copyText(ownerOnboarding.deep_link_url)}>
                      Copy Link
                    </button>
                    <button className="btn secondary" onClick={() => copyOwnerInviteMessage(ownerOnboarding.deep_link_url)}>
                      Copy Message
                    </button>
                    <button className="btn" onClick={() => openTelegramShare(ownerOnboarding.deep_link_url)}>
                      Send via Telegram
                    </button>
                    <QrCodeImage
                      value={ownerOnboarding.deep_link_url}
                      alt="Owner onboarding QR"
                      size={120}
                      className="w-20 h-20 rounded border border-slate-200 p-1 bg-white"
                    />
                  </div>
                </div>
              ) : null}
            </div>
            ) : null}
          </section>

          <section className="panel p-4">
            <h2 className="text-lg font-semibold">Tenant Health</h2>
            <pre className="text-xs bg-slate-100 p-2 rounded mt-2">{JSON.stringify(health.data ?? {}, null, 2)}</pre>
          </section>

          <section className="panel p-4">
            <h2 className="text-lg font-semibold">Tenant Users</h2>
            <div className="grid gap-2 mt-2">
              {(users.data?.items ?? []).map((u) => (
                <article key={u.id} className="rounded border border-slate-200 p-2 bg-white text-sm">
                  {u.email} | {u.role} | {u.is_active ? "active" : "disabled"}
                </article>
              ))}
            </div>
            <div className="grid md:grid-cols-4 gap-2 mt-3">
              <input className="input" placeholder="Email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
              <input className="input" type="password" placeholder="Password" value={userPassword} onChange={(e) => setUserPassword(e.target.value)} />
              <select className="input" value={userRole} onChange={(e) => setUserRole(e.target.value)}>
                <option value="owner">owner</option>
                <option value="admin">admin</option>
                <option value="manager">manager</option>
                <option value="operator">operator</option>
                <option value="viewer">viewer</option>
              </select>
              <button className="btn secondary" onClick={() => createUser.mutate()} disabled={!userEmail || !userPassword}>
                Add User
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Password policy: min 10 with upper/lower/digit.</p>
          </section>

          <section className="panel p-4">
            <h2 className="text-lg font-semibold">Feature Flags</h2>
            <div className="grid md:grid-cols-3 gap-2 mt-2">
              <input className="input" value={newFlagKey} onChange={(e) => setNewFlagKey(e.target.value)} />
              <select className="input" value={newFlagEnabled ? "1" : "0"} onChange={(e) => setNewFlagEnabled(e.target.value === "1")}>
                <option value="1">enabled</option>
                <option value="0">disabled</option>
              </select>
              <button className="btn" onClick={() => upsertFlag.mutate()}>
                Upsert Flag
              </button>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
